import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import PersonSelectorCombobox, { type Person } from '@/components/Combobox/PersonSelectorCombobox';
import Icon from '@/components/Icon/Icon';
import { HStack, Spacer, VStack } from '@/components/Layout';
import { ControlledSelect } from '@/components/Select/ControlledSelect';
import StepperHeaderComponent from '@/components/StepperHeader';
import { Text } from '@/components/Typography/Text';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { CertificationKind } from '../../certification/-constants';
import { AddCertificationStepKeys } from './-constants/constant';
import { CertificationItem, useGetCertifications } from './-hooks/useGetCertifications';
import { useSelectedPersonData } from './-queries/useSelectedPersonData';
export const Route = createFileRoute('/_staff/person/add-person-certification/')({
  component: Introduce
});
type SelectedCertification = {
  certificationId: string;
  certificationKind: CertificationKind;
};
type PersonAndCertificationSelectProps = {
  selectedPerson: Person | null;
  onSelectPerson: (person: Person) => void;
  selectedCertification: SelectedCertification | null;
  setSelectedCertification: (certification: SelectedCertification | null) => void;
  certificationOptions: CertificationItem[] | null;
};
function Introduce() {
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [selectedCertification, setSelectedCertification] = useState<SelectedCertification | null>(null);
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.person.addPersonCertification'
  });
  const {
    data: certificationOptions
  } = useGetCertifications();

  // SearchパラメータにCertificationIdが指定されている場合は、選択済みにする
  const search = useSearch({
    strict: false
  }) as {
    personId?: string;
    certificationId?: string;
  };
  const selectedPersonData = useSelectedPersonData(search.personId);
  useEffect(() => {
    if (selectedPersonData) {
      // oxlint-disable-next-line react/set-state-in-effect -- baseline
      setSelectedPerson({
        id: selectedPersonData.personId,
        name: selectedPersonData.name,
        nameInitials: selectedPersonData.name,
        email: selectedPersonData.primaryWorkerEmail ?? '',
        avatarUrl: selectedPersonData.avatarUrl ?? ''
      });
    }
  }, [selectedPersonData]);
  useEffect(() => {
    if (search.certificationId && certificationOptions) {
      const certification = certificationOptions?.items.find(certification => certification.certificationId === search.certificationId);
      if (certification) {
        // oxlint-disable-next-line react/set-state-in-effect -- baseline
        setSelectedCertification({
          certificationId: certification.certificationId,
          certificationKind: certification.certificationKind
        });
      }
    }
  }, [certificationOptions, search.certificationId]);
  const navigate = useNavigate();
  const handleButtonClick = useCallback(() => {
    if (selectedCertification && selectedPerson) {
      const certificationKind = selectedCertification.certificationKind;
      navigate({
        to: '/person/add-person-certification/$certificationKind',
        params: {
          certificationKind
        },
        search: {
          personId: selectedPerson.id,
          certificationId: selectedCertification.certificationId
        },
        hash: certificationKind === CertificationKind.BASE ? AddCertificationStepKeys.PERSON_BASE_CERTIFICATION_BASIC_INFO : certificationKind === CertificationKind.SPECIALTY ? AddCertificationStepKeys.PERSON_SPECIALTY_CERTIFICATION_BASIC_INFO : AddCertificationStepKeys.PERSON_HANDLING_CERTIFICATION_BASIC_INFO
      });
    }
  }, [selectedCertification, selectedPerson, navigate]);
  const handleAddCertification = () => {
    navigate({
      to: '/certification/add'
    });
  };
  return <VStack className="mx-auto w-[1000px] gap-3">
      <Header />
      <PersonAndCertificationSelectSection selectedPerson={selectedPerson} onSelectPerson={setSelectedPerson} selectedCertification={selectedCertification} setSelectedCertification={setSelectedCertification} certificationOptions={certificationOptions?.items ?? null} />
      <Spacer variant="xl" />
      <ActionButton icon="FilePenIcon" className="w-48" onClick={handleButtonClick} disabled={!selectedCertification || !selectedPerson}>
        {t('add.intro.body.fillOutDetails')}
      </ActionButton>
      <Spacer variant="xxl" />
      <VStack className="w-full">
        <Text variant="labelSemiLarge" className="text-neutral-muted-on-fill">
          {t('add.intro.options.description')}
        </Text>
        <Spacer size={24} />
        <ActionButton variant="ghost" className="relative w-full justify-start p-0" onClick={handleAddCertification}>
          <HStack className="items-center justify-between w-full">
            <Icon iconName="PlusIcon" text={t('add.intro.options.addCertification')} textColor="text-primary" colorVariant="primary" />
            <Icon iconName="ChevronRight" className="m-[10px]" />
          </HStack>
        </ActionButton>
        <Separator className="my-2" />
      </VStack>
    </VStack>;
}
function Header() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.person.addPersonCertification.add.intro.header'
  });
  return <VStack>
      <Spacer variant="xxl" />
      <StepperHeaderComponent headerTitle={t('headerTitle')} />
    </VStack>;
}
function PersonAndCertificationSelectSection({
  selectedPerson,
  onSelectPerson,
  selectedCertification,
  setSelectedCertification,
  certificationOptions
}: PersonAndCertificationSelectProps) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.person.addPersonCertification.add.intro.body'
  });
  const [popoverOpen, setPopoverOpen] = useState(false);
  return <>
      <Spacer variant="xl" />
      <Text variant="h4">{t('selectPersonAndCertification')}</Text>
      <Spacer variant="l" />
      <VStack className="gap-2">
        <HStack className="items-center gap-7">
          <VStack className="gap-3">
            <Text variant="labelReg" className="text-neutral-muted-on-fill">
              {t('selectPerson')}
            </Text>
            <HStack>
              <Popover open={popoverOpen} onOpenChange={setPopoverOpen} modal={true}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[300px] justify-between bg-white">
                    <Text variant="labelReg">
                      {selectedPerson ? selectedPerson.name : t('selectPersonPlaceholder')}
                    </Text>
                    <Icon iconName="ChevronDownIcon" className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start" side="bottom" sideOffset={5}>
                  <div className="max-h-[300px] overflow-y-auto">
                    <PersonSelectorCombobox onSelect={person => {
                    onSelectPerson(person);
                    setPopoverOpen(false);
                  }} onClose={() => setPopoverOpen(false)} />
                  </div>
                </PopoverContent>
              </Popover>
            </HStack>
          </VStack>
          <VStack className="gap-3">
            <Text variant="labelReg" className="text-neutral-muted-on-fill">
              {t('selectCertification')}
            </Text>
            <HStack>
              <ControlledSelect id="certificationKind" width={300} value={selectedCertification?.certificationId ?? undefined} onChange={option => {
              const selectedCertification = certificationOptions?.find(certification => certification.certificationId === option?.value) ?? null;
              setSelectedCertification(selectedCertification);
            }} fetchOptions={certificationOptions ? (text?: string) => Promise.resolve(certificationOptions.filter(option => option.certificationName.includes(text ?? '')).map(option => ({
              value: option.certificationId,
              label: option.certificationName
            })) ?? []) : undefined} className="bg-white" />
            </HStack>
          </VStack>
        </HStack>
      </VStack>
    </>;
}